import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvent } from '../context/EventContext';
import { useUserProfile } from '../hooks/useUserProfile';
import NotificationModal from '../components/NotificationModal';
import enhancedApiClient from '../api/enhancedApiClient';

const Dashboard = () => {
  const navigate = useNavigate();
  const { eventData, tasks, vendorCount, guestCount, updateEvent, refreshTasks, loading: eventLoading } = useEvent();
  const [selectedTask, setSelectedTask] = useState(null);
  
  useEffect(() => {
    if (tasks.length > 0 && !selectedTask) {
      const completed = tasks.filter(t => t.status === 'completed');
      const uncompleted = tasks.filter(t => t.status !== 'completed');
      const lastCompleted = completed.length > 0 ? [completed[completed.length - 1]] : [];
      const nextUncompleted = uncompleted.slice(0, 2);
      const displayTasks = [...lastCompleted, ...nextUncompleted];
      setSelectedTask(displayTasks[0]);
    }
  }, [tasks, selectedTask]);
  const [editingField, setEditingField] = useState(null);
  const [tempValues, setTempValues] = useState({});
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [venueName, setVenueName] = useState('');
  const [guests, setGuests] = useState([]);
  const [nextPayment, setNextPayment] = useState(null);
  const [budgetStats, setBudgetStats] = useState({ spent: 0, remaining: 0, percentage: 0 });
  const [hoveredBudgetSection, setHoveredBudgetSection] = useState(null);
  
  // Get current user from AWS Cognito
  const [currentUser, setCurrentUser] = useState(null);
  
  useEffect(() => {
    const getCurrentUser = async () => {
      try {
        const { Auth } = await import('aws-amplify');
        const user = await Auth.currentAuthenticatedUser();
        setCurrentUser(user);
      } catch (error) {
        console.log('No authenticated user');
      }
    };
    getCurrentUser();
  }, []);
  
  const { userProfile, loading: profileLoading } = useUserProfile(currentUser?.username);

  useEffect(() => {
    const loadVenue = async () => {
      if (!eventData?.eventId) return;
      try {
        const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${eventData.eventId}/vendors`);
        const vendors = await response.json();
        const venue = vendors.find(v => v.vendorType === 'venue');
        if (venue) setVenueName(venue.name);
      } catch (error) {
        console.error('Error loading venue:', error);
      }
    };
    loadVenue();
  }, [eventData?.eventId]);

  useEffect(() => {
    const loadGuests = async () => {
      if (!eventData?.eventId) return;
      try {
        const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/guests/${eventData.eventId}`);
        if (response.ok) {
          const guestsData = await response.json();
          const standardizedGuests = guestsData.map(guest => {
            const standardized = {};
            for (const [key, value] of Object.entries(guest)) {
              if (value?.S !== undefined) standardized[key] = value.S;
              else if (value?.N !== undefined) standardized[key] = parseInt(value.N);
              else if (value?.BOOL !== undefined) standardized[key] = value.BOOL;
              else standardized[key] = value;
            }
            return standardized;
          });
          setGuests(standardizedGuests);
        }
      } catch (error) {
        console.error('Error loading guests:', error);
      }
    };
    loadGuests();
  }, [eventData?.eventId]);

  useEffect(() => {
    const loadNextPayment = async () => {
      if (!eventData?.eventId) return;
      try {
        const response = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/invoices?eventId=${eventData.eventId}`);
        console.log('Invoices API response status:', response.status);
        if (response.ok) {
          const invoices = await response.json();
          console.log('Invoices received:', invoices);
          
          // Calculate budget stats
          const totalSpent = invoices.reduce((sum, inv) => {
            const amount = parseFloat(inv.total) || parseFloat(inv.depositAmount) || 0;
            return sum + amount;
          }, 0);
          
          const budget = eventData?.budget || 0;
          const remaining = budget - totalSpent;
          const percentage = budget > 0 ? Math.round((totalSpent / budget) * 100) : 0;
          
          setBudgetStats({
            spent: totalSpent,
            remaining: remaining,
            percentage: percentage
          });
          
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          // Filter to unpaid invoices with future due dates and valid amounts
          const unpaidInvoices = invoices.filter(inv => {
            const isUnpaid = inv.status !== 'paid';
            const hasDueDate = inv.dueDate && inv.dueDate !== '';
            const dueDate = new Date(inv.dueDate);
            const isFuture = dueDate >= today;
            const hasAmount = (inv.total && inv.total > 0) || (inv.depositAmount && inv.depositAmount > 0);
            console.log(`Invoice ${inv.name}: unpaid=${isUnpaid}, hasDueDate=${hasDueDate}, isFuture=${isFuture}, hasAmount=${hasAmount}`);
            return isUnpaid && hasDueDate && isFuture && hasAmount;
          });
          
          console.log('Unpaid invoices with future due dates:', unpaidInvoices);
          
          if (unpaidInvoices.length > 0) {
            // Sort by due date (earliest first)
            const sorted = unpaidInvoices.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
            console.log('Next payment:', sorted[0]);
            setNextPayment(sorted[0]);
          } else {
            console.log('No upcoming payments found');
            setNextPayment(null);
          }
        } else {
          console.error('API response not OK:', await response.text());
        }
      } catch (error) {
        console.error('Error loading payments:', error);
      }
    };
    loadNextPayment();
  }, [eventData?.eventId, eventData?.budget]);

  const getDaysUntilWedding = () => {
    if (!eventData?.eventDate) return null;
    const wedding = new Date(eventData.eventDate);
    const today = new Date();
    const diffTime = wedding - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getTaskDueDateBadge = (dueDate) => {
    if (!dueDate) return null;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return {
        text: `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) === 1 ? '' : 's'}`,
        emoji: '🔴',
        color: '#DC2626',
        bgColor: '#FEE2E2'
      };
    } else if (diffDays === 0) {
      return {
        text: 'Due today',
        emoji: '🟡',
        color: '#F59E0B',
        bgColor: '#FEF3C7'
      };
    } else if (diffDays === 1) {
      return {
        text: 'Due tomorrow',
        emoji: '🟡',
        color: '#F59E0B',
        bgColor: '#FEF3C7'
      };
    } else if (diffDays <= 7) {
      return {
        text: `Due in ${diffDays} days`,
        emoji: '🟡',
        color: '#F59E0B',
        bgColor: '#FEF3C7'
      };
    } else {
      return {
        text: `Due in ${diffDays} days`,
        emoji: '',
        color: '#6B7280',
        bgColor: 'transparent'
      };
    }
  };

  const getTaskImage = (task) => {
    if (!task) return 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop';
    
    const name = task.name?.toLowerCase() || '';
    if (name.includes('menu')) return 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=800&h=600&fit=crop';
    if (name.includes('flower') || name.includes('floral')) return 'https://images.unsplash.com/photo-1520763185298-1b434c919102?w=800&h=600&fit=crop';
    if (name.includes('seating') || name.includes('plan')) return 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?w=800&h=600&fit=crop';
    if (name.includes('music') || name.includes('band')) return 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=800&h=600&fit=crop';
    if (name.includes('photo') || name.includes('photographer')) return 'https://images.unsplash.com/photo-1606800052052-a08af7148866?w=800&h=600&fit=crop';
    return 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=600&fit=crop';
  };

  const handleEdit = (field, value) => {
    setEditingField(field);
    setTempValues({ ...tempValues, [field]: value });
  };

  const handleSave = async (field) => {
    const success = await updateEvent({ [field]: tempValues[field] });
    if (success) {
      alert('✅ Updated successfully!');
    } else {
      alert('❌ Failed to update. Backend unavailable.');
    }
    setEditingField(null);
  };

  const handleCancel = () => {
    setEditingField(null);
    setTempValues({});
  };

  const daysUntil = getDaysUntilWedding();

  if (eventLoading || profileLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        gap: '16px'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #E5E7EB',
          borderTop: '3px solid #6B9080',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <div style={{ color: '#6B7280', fontSize: '16px' }}>
          Loading dashboard...
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Dashboard Header with Sam & Taylor and Stats Cards */}
      <div className="page-header" style={{
        backgroundColor: '#E8F4E8',
        padding: '32px 24px 140px 24px',
        textAlign: 'center',
        position: 'relative',
        marginBottom: '16px'
      }}>
        <h1 className="page-title" style={{ 
          fontSize: '42px', 
          fontWeight: '400', 
          color: '#1F2937',
          margin: 0,
          fontFamily: 'Georgia, serif',
          letterSpacing: '-0.02em'
        }}>
          {eventData?.clientName}
        </h1>
        
        {/* Subtitle - positioned absolutely to not affect layout */}
        <div style={{ 
          position: 'absolute',
          top: '85px',
          left: '50%',
          transform: 'translateX(-50%)',
          fontSize: '14px', 
          color: '#6B7280', 
          fontWeight: '400'
        }}>
          {daysUntil === 0 ? 'Event day!' : daysUntil > 0 ? `${daysUntil} days to go` : `${Math.abs(daysUntil)} days ago`}{venueName && ` • ${venueName}`}
        </div>
        
        {/* Stats Cards positioned at bottom of green area */}
        <div className="stats-grid" style={{ 
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'grid', 
          gridTemplateColumns: 'repeat(2, 1fr)', 
          gap: '16px',
          width: '100%',
          maxWidth: '1200px',
          padding: '0 24px'
        }}>
          <div className="stats-card" style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }} onClick={() => navigate('/budget')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                ${Math.round((eventData?.budget || 0) / 1000)}k <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>total budget</span>
              </div>
            </div>
            <div style={{ marginLeft: 'auto', fontSize: '16px', color: '#6B7280' }}>→</div>
          </div>
          
          <div style={{
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#1F2937', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
                  {guests.filter(g => g.rsvpStatus === 'confirmed').length}/{guestCount} <span style={{ fontSize: '18px', color: '#000000', fontWeight: 'normal' }}>guests confirmed</span>
                </div>
              </div>
            </div>
            <div style={{ 
              width: '100%', 
              height: '6px', 
              backgroundColor: '#E5E7EB', 
              borderRadius: '3px',
              overflow: 'hidden'
            }}>
              <div style={{
                width: `${guestCount > 0 ? (guests.filter(g => g.rsvpStatus === 'confirmed').length / guestCount * 100) : 0}%`,
                height: '100%',
                backgroundColor: '#6B9080',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Tracker */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '0 24px 0px 24px',
        marginBottom: '28px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '16px 20px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: 'none'
        }}>
          <div style={{ textAlign: 'center', marginBottom: '12px' }}>
            <div style={{ fontSize: '20px', fontWeight: '600', color: '#6B9080', marginBottom: '2px' }}>
              {tasks.length > 0 ? Math.round((tasks.filter(t => t.status === 'completed').length / tasks.length) * 100) : 0}% Complete
            </div>
            <div style={{ fontSize: '13px', color: '#6B7280' }}>
              {tasks.length > 0 && tasks.filter(t => t.status === 'completed').length >= tasks.length * 0.75 
                ? "Almost there! Your big day is coming together beautifully" 
                : tasks.length > 0 && tasks.filter(t => t.status === 'completed').length >= tasks.length * 0.5
                ? "You're doing amazing! More than halfway there"
                : tasks.length > 0 && tasks.filter(t => t.status === 'completed').length >= tasks.length * 0.25
                ? "Great start! You've got this"
                : "Let's bring your dream wedding to life!"}
            </div>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            position: 'relative',
            marginBottom: '10px'
          }}>
            <div style={{
              position: 'absolute',
              top: '14px',
              left: '0',
              right: '0',
              height: '2px',
              backgroundColor: '#E5E7EB',
              zIndex: 0
            }}>
              <div style={{
                height: '100%',
                width: `${tasks.length > 0 ? (tasks.filter(t => t.status === 'completed').length / tasks.length) * 100 : 0}%`,
                backgroundColor: '#6B9080',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
            {[0, 0.25, 0.5, 0.75, 1].map((milestone, idx) => {
              const isReached = tasks.length > 0 && (tasks.filter(t => t.status === 'completed').length / tasks.length) >= milestone;
              return (
                <div key={idx} style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  zIndex: 1
                }}>
                  <div style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    backgroundColor: isReached ? '#6B9080' : 'white',
                    border: `2px solid ${isReached ? '#6B9080' : '#E5E7EB'}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '5px',
                    transition: 'all 0.3s ease'
                  }}>
                    {isReached ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                        <polyline points="20,6 9,17 4,12"></polyline>
                      </svg>
                    ) : (
                      <div style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#E5E7EB' }}></div>
                    )}
                  </div>
                  <div style={{ fontSize: '10px', color: isReached ? '#6B9080' : '#6B7280', fontWeight: '500', textAlign: 'center' }}>
                    {milestone === 0 ? 'Start' : milestone === 0.25 ? '25%' : milestone === 0.5 ? 'Halfway' : milestone === 0.75 ? '75%' : 'Done!'}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Dashboard Content */}
      <div className="container" style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 24px 48px 24px', marginTop: '32px' }}>
        <div className="dashboard-grid" style={{ 
          display: 'flex',
          gap: '32px',
          alignItems: 'flex-start',
          justifyContent: 'space-between'
        }}>
          {/* Left Panel - Your Wedding Plan */}
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            width: '340px',
            flexShrink: 0,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', margin: 0 }}>Your wedding plan</h2>
              <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
                {tasks.filter(t => t.status === 'completed').length}/{tasks.length}
              </span>
            </div>

            {/* Timeline Stepper */}
            {(() => {
              const phases = [
                { label: '6+ mo', key: '6plus' },
                { label: '3-6 mo', key: '3to6' },
                { label: '1-3 mo', key: '1to3' },
                { label: 'Final', key: 'final' }
              ];

              const getTimeGroup = (dueDate) => {
                if (!dueDate || !eventData?.eventDate) return null;
                const due = new Date(dueDate);
                const event = new Date(eventData.eventDate);
                const diffMonths = (event - due) / (1000 * 60 * 60 * 24 * 30);
                if (diffMonths < 1) return 'final';
                if (diffMonths < 3) return '1to3';
                if (diffMonths < 6) return '3to6';
                return '6plus';
              };

              // Determine current active phase based on earliest uncompleted task
              const uncompleted = tasks.filter(t => t.status !== 'completed').sort((a, b) => {
                if (!a.dueDate) return 1;
                if (!b.dueDate) return -1;
                return new Date(a.dueDate) - new Date(b.dueDate);
              });
              const currentPhase = uncompleted.length > 0 ? getTimeGroup(uncompleted[0].dueDate) : 'final';
              const currentPhaseIndex = phases.findIndex(p => p.key === currentPhase);

              // Use selected phase if set, otherwise use current phase
              const activePhase = selectedPhase || currentPhase;
              const activePhaseIndex = phases.findIndex(p => p.key === activePhase);

              // Get tasks for active phase
              const phaseTasks = uncompleted.filter(t => getTimeGroup(t.dueDate) === activePhase).slice(0, 3);

              const getTaskIcon = (taskName) => {
                const name = (taskName || '').toLowerCase();
                if (name.includes('menu') || name.includes('catering') || name.includes('food') || name.includes('dietary'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/></svg>;
                if (name.includes('flower') || name.includes('floral') || name.includes('décor') || name.includes('styling'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M12 2a4 4 0 0 1 4 4c0 1.95-1.4 3.58-3.25 3.93"/><path d="M12 2a4 4 0 0 0-4 4c0 1.95 1.4 3.58 3.25 3.93"/><path d="M22 12a4 4 0 0 1-4 4c-1.95 0-3.58-1.4-3.93-3.25"/><path d="M22 12a4 4 0 0 0-4-4c-1.95 0-3.58 1.4-3.93 3.25"/><path d="M12 22a4 4 0 0 1-4-4c0-1.95 1.4-3.58 3.25-3.93"/><path d="M12 22a4 4 0 0 0 4-4c0-1.95-1.4-3.58-3.25-3.93"/><path d="M2 12a4 4 0 0 1 4-4c1.95 0 3.58 1.4 3.93 3.25"/><path d="M2 12a4 4 0 0 0 4 4c1.95 0 3.58-1.4 3.93-3.25"/></svg>;
                if (name.includes('photo') || name.includes('video') || name.includes('camera'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>;
                if (name.includes('music') || name.includes('dj') || name.includes('band') || name.includes('entertainment'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>;
                if (name.includes('guest') || name.includes('rsvp') || name.includes('invitation') || name.includes('seating'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
                if (name.includes('payment') || name.includes('budget') || name.includes('invoice') || name.includes('deposit') || name.includes('balance') || name.includes('cost'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>;
                if (name.includes('contract') || name.includes('sign') || name.includes('document'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14,2 14,8 20,8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>;
                if (name.includes('venue') || name.includes('floor plan') || name.includes('layout') || name.includes('access'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V9.5l-6-2.5"/></svg>;
                if (name.includes('dress') || name.includes('suit') || name.includes('attire') || name.includes('fitting'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M20.38 3.46L16 2 12 5.5 8 2l-4.38 1.46a2 2 0 0 0-1.34 2.23l2.1 8.09A2 2 0 0 0 6.32 15H8l-1 6h10l-1-6h1.68a2 2 0 0 0 1.94-1.22l2.1-8.09a2 2 0 0 0-1.34-2.23z"/></svg>;
                if (name.includes('cake'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M20 21v-8a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8"/><path d="M4 16s.5-1 2-1 2.5 2 4 2 2.5-2 4-2 2.5 2 4 2 2-1 2-1"/><path d="M2 21h20"/><path d="M7 8v3"/><path d="M12 8v3"/><path d="M17 8v3"/><path d="M7 4h.01"/><path d="M12 4h.01"/><path d="M17 4h.01"/></svg>;
                if (name.includes('timeline') || name.includes('run sheet') || name.includes('schedule') || name.includes('timing'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></svg>;
                if (name.includes('ceremony') || name.includes('weather') || name.includes('rehearsal'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z"/></svg>;
                if (name.includes('vendor') || name.includes('quote') || name.includes('book'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>;
                if (name.includes('thank') || name.includes('review') || name.includes('message'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>;
                if (name.includes('transport') || name.includes('arrival'))
                  return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M5 17h14v-5H5z"/><path d="M2 17h20"/><circle cx="7.5" cy="17" r="2.5"/><circle cx="16.5" cy="17" r="2.5"/><path d="M5 12V7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v5"/></svg>;
                // Default checklist icon
                return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="1.5"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>;
              };

              return (
                <>
                  {/* Stepper */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '24px', position: 'relative' }}>
                    {phases.map((phase, i) => {
                      const isActive = i === activePhaseIndex;
                      const isCompleted = i < currentPhaseIndex;
                      return (
                        <div key={phase.key} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', cursor: 'pointer' }}
                          onClick={() => setSelectedPhase(phase.key)}
                        >
                          {i > 0 && (
                            <div style={{
                              position: 'absolute', top: '16px', right: '50%', width: '100%', height: '2px',
                              backgroundColor: isCompleted || isActive ? '#6B9080' : '#E5E7EB', zIndex: 0
                            }} />
                          )}
                          <div style={{
                            width: '32px', height: '32px', borderRadius: '50%',
                            backgroundColor: isActive ? '#6B9080' : isCompleted ? '#6B9080' : 'white',
                            border: isActive || isCompleted ? 'none' : '2px solid #E5E7EB',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '14px', fontWeight: '600',
                            color: isActive || isCompleted ? 'white' : '#9CA3AF',
                            zIndex: 1, position: 'relative'
                          }}>
                            {isCompleted ? '✓' : i + 1}
                          </div>
                          <div style={{ fontSize: '11px', color: isActive ? '#1F2937' : '#9CA3AF', fontWeight: isActive ? '600' : '400', marginTop: '6px', textAlign: 'center' }}>
                            {phase.label}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Task Cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                    {phaseTasks.length > 0 ? phaseTasks.map((task, index) => (
                      <div
                        key={task.id}
                        onClick={() => setSelectedTask(task)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '12px',
                          cursor: 'pointer', transition: 'all 0.2s'
                        }}
                      >
                        <div
                          style={{
                            width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                            backgroundColor: task.status === 'completed' ? '#6B9080' : 'white',
                            border: task.status === 'completed' ? 'none' : '2px solid #D1D5DB',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: 'white', fontSize: '14px', cursor: 'pointer'
                          }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const newStatus = task.status === 'completed' ? 'pending' : 'completed';
                              await enhancedApiClient.updateTask(eventData?.eventId, task.id, { status: newStatus });
                              await refreshTasks();
                              setNotification({ show: true, message: newStatus === 'completed' ? 'Task completed!' : 'Task marked as pending', type: 'success' });
                              if (newStatus === 'completed') setSelectedTask(null);
                            } catch (error) {
                              setNotification({ show: true, message: 'Failed to update task.', type: 'error' });
                            }
                          }}
                        >
                          {task.status === 'completed' ? '✓' : ''}
                        </div>
                        <div style={{
                          flex: 1, padding: '14px 16px', borderRadius: '10px',
                          backgroundColor: selectedTask?.id === task.id ? '#E8F4E8' : '#F8FAFC',
                          border: selectedTask?.id === task.id ? '1.5px solid #6B9080' : '1.5px solid transparent',
                          display: 'flex', alignItems: 'center', gap: '12px',
                          transition: 'all 0.15s'
                        }}
                        onMouseOver={(e) => { if (selectedTask?.id !== task.id) e.currentTarget.style.backgroundColor = '#F1F5F9'; }}
                        onMouseOut={(e) => { if (selectedTask?.id !== task.id) e.currentTarget.style.backgroundColor = '#F8FAFC'; }}
                        >
                          <div style={{
                            width: '36px', height: '36px', borderRadius: '8px',
                            backgroundColor: '#E8F4E8', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0
                          }}>
                            {getTaskIcon(task.name)}
                          </div>
                          <div style={{ fontWeight: '500', fontSize: '14px', color: '#1F2937', lineHeight: '1.3' }}>
                            {task.name}
                          </div>
                          {selectedTask?.id === task.id && (
                            <div style={{ marginLeft: 'auto', color: '#6B9080', fontSize: '16px', flexShrink: 0 }}>›</div>
                          )}
                        </div>
                      </div>
                    )) : (
                      <div style={{ padding: '16px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' }}>
                        All tasks in this phase are complete!
                      </div>
                    )}
                  </div>
                </>
              );
            })()}

            <button
              onClick={() => navigate('/checklist')}
              style={{
                width: '100%', padding: '12px', backgroundColor: 'transparent', border: 'none',
                color: '#6B7280', cursor: 'pointer', display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', fontSize: '14px'
              }}
            >
              View full checklist ({tasks.length} items)
              <span>→</span>
            </button>
          </div>

          {/* Center Panel - Task Preview (Biggest) */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            flex: '1',
            maxWidth: '600px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div 
              onClick={() => {
                const task = selectedTask || tasks[0];
                if (task?.id) navigate(`/checklist?taskId=${task.id}`);
              }}
              style={{
              height: '140px',
              background: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url("${getTaskImage(selectedTask || tasks[0])}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              cursor: 'pointer'
            }}>
              <h2 style={{ 
                fontSize: '32px', 
                fontWeight: '400',
                textAlign: 'center',
                margin: 0,
                fontFamily: 'Georgia, serif'
              }}>
                {(selectedTask || tasks[0])?.name || 'Plan Your Wedding'}
              </h2>
            </div>
            
            <div style={{ padding: '20px' }}>
              {(selectedTask || tasks[0]) ? (
                <>
                  <p style={{ 
                    color: '#6B7280', 
                    lineHeight: '1.6',
                    marginBottom: '20px',
                    fontSize: '14px'
                  }}>
                    {(selectedTask || tasks[0])?.details || `Review and complete: ${(selectedTask || tasks[0])?.name}`}
                  </p>
                  
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      style={{
                        flex: 1,
                        padding: '12px 24px',
                        backgroundColor: '#6B9080',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#5A7A6A'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#6B9080'}
                      onClick={async () => {
                        const task = selectedTask || tasks[0];
                        try {
                          const newStatus = task.status === 'completed' ? 'pending' : 'completed';
                          await enhancedApiClient.updateTask(eventData?.eventId, task.id, { status: newStatus });
                          await refreshTasks();
                          
                          setNotification({
                            show: true,
                            message: newStatus === 'completed' ? 'Task marked as completed!' : 'Task marked as pending',
                            type: 'success'
                          });
                          
                          setSelectedTask(null);
                        } catch (error) {
                          setNotification({
                            show: true,
                            message: 'Failed to update task. Please try again.',
                            type: 'error'
                          });
                        }
                      }}
                    >
                      {(selectedTask || tasks[0])?.status === 'completed' ? 'Mark as Pending' : 'Mark Complete'}
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ padding: '40px', textAlign: 'center', color: '#6B7280' }}>
                  <h3 style={{ fontSize: '24px', fontFamily: 'Georgia, serif', marginBottom: '12px' }}>Plan Your Wedding</h3>
                  <p>Select a task from your checklist to get started</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Payments */}
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '12px',
            width: '300px',
            flexShrink: 0,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              marginBottom: '20px',
              color: '#1F2937'
            }}>
              Budget
            </h2>
            
            {nextPayment ? (
              <div style={{
                backgroundColor: '#E8F4E8',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #6B9080',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B9080" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#1F2937' }}>
                    NEXT PAYMENT DUE
                  </div>
                </div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: '#1F2937', marginBottom: '4px' }}>
                  ${nextPayment.total || nextPayment.depositAmount || 0}
                </div>
                <div style={{ fontSize: '13px', color: '#1F2937', marginBottom: '4px' }}>
                  {nextPayment.name || 'Invoice'}
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '12px' }}>
                  Due {new Date(nextPayment.dueDate).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
                <button
                  onClick={() => navigate('/budget', { state: { invoiceId: nextPayment.id, categoryId: nextPayment.categoryId } })}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#6B9080',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  View Payment
                </button>
              </div>
            ) : (
              <div style={{ padding: '20px', textAlign: 'center', color: '#6B7280', fontSize: '14px' }}>
                No upcoming payments
              </div>
            )}
            
            <div style={{ paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
              <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '12px' }}>Budget Overview</div>
              
              {/* Progress Bar */}
              <div style={{ marginBottom: '16px', position: 'relative' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  marginBottom: '6px'
                }}>
                  <div style={{ fontSize: '11px', color: '#6B7280' }}>
                    $0
                  </div>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '600',
                    color: '#1F2937'
                  }}>
                    ${(eventData?.budget || 0).toLocaleString()}
                  </div>
                  {budgetStats.percentage > 100 && (
                    <div style={{ 
                      fontSize: '11px', 
                      fontWeight: '600',
                      color: '#DC2626'
                    }}>
                      ${budgetStats.spent.toLocaleString()}
                    </div>
                  )}
                </div>
                
                {/* Tooltip */}
                {hoveredBudgetSection && (
                  <div style={{
                    position: 'absolute',
                    top: '30px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#1F2937',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: '500',
                    whiteSpace: 'nowrap',
                    zIndex: 10,
                    pointerEvents: 'none'
                  }}>
                    {hoveredBudgetSection}
                  </div>
                )}
                
                <div style={{ 
                  width: '100%', 
                  height: '8px', 
                  backgroundColor: '#E5E7EB', 
                  borderRadius: '4px',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {budgetStats.percentage > 100 ? (
                    <>
                      {/* Green section with tooltip */}
                      <div 
                        style={{
                          width: `${(100 / budgetStats.percentage) * 100}%`,
                          height: '100%',
                          backgroundColor: hoveredBudgetSection === `Budget: $${(eventData?.budget || 0).toLocaleString()}` ? '#5A7A6A' : '#6B9080',
                          transition: 'all 0.3s ease',
                          position: 'absolute',
                          left: 0,
                          cursor: 'pointer'
                        }}
                        onMouseEnter={() => setHoveredBudgetSection(`Budget: $${(eventData?.budget || 0).toLocaleString()}`)}
                        onMouseLeave={() => setHoveredBudgetSection(null)}
                      ></div>
                      
                      {/* Red section with tooltip */}
                      <div 
                        style={{
                          width: `${100 - (100 / budgetStats.percentage) * 100}%`,
                          height: '100%',
                          backgroundColor: hoveredBudgetSection === `Over budget by $${Math.abs(budgetStats.remaining).toLocaleString()}` ? '#B91C1C' : '#DC2626',
                          transition: 'all 0.3s ease',
                          position: 'absolute',
                          right: 0,
                          cursor: 'pointer'
                        }}
                        onMouseEnter={() => setHoveredBudgetSection(`Over budget by $${Math.abs(budgetStats.remaining).toLocaleString()}`)}
                        onMouseLeave={() => setHoveredBudgetSection(null)}
                      ></div>
                    </>
                  ) : (
                    /* Green bar only when within budget */
                    <div 
                      style={{
                        width: `${budgetStats.percentage}%`,
                        height: '100%',
                        backgroundColor: hoveredBudgetSection ? '#5A7A6A' : '#6B9080',
                        transition: 'all 0.3s ease',
                        borderRadius: '4px',
                        cursor: 'pointer'
                      }}
                      onMouseEnter={() => setHoveredBudgetSection(`Spent: $${budgetStats.spent.toLocaleString()} of $${(eventData?.budget || 0).toLocaleString()}`)}
                      onMouseLeave={() => setHoveredBudgetSection(null)}
                    ></div>
                  )}
                  
                  {/* Budget limit marker */}
                  <div style={{
                    position: 'absolute',
                    right: budgetStats.percentage > 100 ? `${100 - (100 / budgetStats.percentage) * 100}%` : 0,
                    top: '-6px',
                    width: '2px',
                    height: '20px',
                    backgroundColor: '#1F2937',
                    zIndex: 2
                  }}></div>
                </div>
              </div>
              
              <button
                onClick={() => navigate('/budget')}
                style={{
                  width: '100%',
                  padding: '10px',
                  backgroundColor: 'white',
                  color: '#6B9080',
                  border: '1px solid #6B9080',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                View All Budget Items
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <NotificationModal
        isOpen={notification.show}
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ show: false, message: '', type: 'success' })}
      />
      
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;